import React from 'react';
import { Player } from '../../types';
import { KeywordBadge } from '../ui/KeywordBadge';

interface PlayerCardProps {
  player: Player;
  isCurrentTurn: boolean;
  isCurrentUser: boolean;
}

export const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  isCurrentTurn,
  isCurrentUser,
}) => {
  const { character, stats } = player;

  if (!character) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="text-center">
          <div className="font-medium text-gray-900">{player.username}</div>
          <div className="text-sm text-gray-500">No character selected</div>
        </div>
      </div>
    );
  }

  const getHealthColor = (health: number, maxHealth: number) => {
    const percentage = (health / maxHealth) * 100;
    if (percentage > 75) return 'text-green-600';
    if (percentage > 50) return 'text-yellow-600';
    if (percentage > 25) return 'text-orange-600';
    return 'text-red-600';
  };

  const getWillColor = (will: number, maxWill: number) => {
    const percentage = (will / maxWill) * 100;
    if (percentage > 75) return 'text-blue-600';
    if (percentage > 50) return 'text-blue-500';
    if (percentage > 25) return 'text-blue-400';
    return 'text-blue-300';
  };

  return (
    <div
      className={`border rounded-lg p-4 transition-all ${
        stats.isEliminated
          ? 'bg-red-50 border-red-200 opacity-75'
          : isCurrentTurn
          ? 'bg-green-50 border-green-300 shadow-md'
          : isCurrentUser
          ? 'bg-blue-50 border-blue-300'
          : 'bg-white border-gray-200'
      }`}
    >
      {/* Player Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            {player.username}
            {isCurrentUser && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                You
              </span>
            )}
            {isCurrentTurn && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Current Turn
              </span>
            )}
          </div>
          <div className="text-sm text-gray-600">{character.name}</div>
          <div className="text-xs text-gray-500">{character.type}</div>
        </div>
        
        {stats.isEliminated && (
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-medium">
            ELIMINATED
          </span>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* Health */}
        <div className="text-center">
          <div className={`text-xl font-bold ${getHealthColor(stats.health, character.baseStats.health)}`}>
            {stats.health}
          </div>
          <div className="text-xs text-gray-600">
            Health (/{character.baseStats.health})
          </div>
        </div>

        {/* Will */}
        <div className="text-center">
          <div className={`text-xl font-bold ${getWillColor(stats.will, character.baseStats.will)}`}>
            {stats.will}
          </div>
          <div className="text-xs text-gray-600">
            Will (/{character.baseStats.will})
          </div>
        </div>

        {/* Attack Bonus */}
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">
            +{stats.attackBonus}
          </div>
          <div className="text-xs text-gray-600">Attack</div>
        </div>

        {/* Defense Bonus */}
        <div className="text-center">
          <div className="text-lg font-bold text-purple-600">
            +{stats.defenseBonus}
          </div>
          <div className="text-xs text-gray-600">Defense</div>
        </div>
      </div>

      {/* Speed and Phases */}
      <div className="flex justify-between items-center mb-3 text-sm">
        <div className="text-center">
          <div className="font-bold text-yellow-600">{stats.speed}</div>
          <div className="text-xs text-gray-600">Speed</div>
        </div>
        
        {isCurrentTurn && (
          <div className="text-center">
            <div className="font-bold text-blue-600">{stats.phasesRemaining}</div>
            <div className="text-xs text-gray-600">Phases</div>
          </div>
        )}
      </div>

      {/* Status Effects */}
      {(stats.activeEffects.length > 0 || stats.isKnockedDown) && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700 mb-1">Status:</div>
          <div className="flex flex-wrap gap-1">
            {stats.isKnockedDown && (
              <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                Knocked Down
              </span>
            )}
            {stats.activeEffects.map((effect, index) => (
              <span
                key={index}
                className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded"
              >
                {effect.type}
                {effect.duration > 0 && ` (${effect.duration})`}
                {effect.value && ` ${effect.value}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Character Keywords */}
      {character.keywords.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-medium text-gray-700 mb-1">Keywords:</div>
          <div className="flex flex-wrap gap-1">
            {character.keywords.map((keyword, index) => (
              <span
                key={index}
                className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
              >
                <KeywordBadge
                  keyword={keyword.name}
                  value={keyword.value}
                  showTooltip={true}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Character Immunities */}
      {character.immunities.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Immunities:</div>
          <div className="flex flex-wrap gap-1">
            {character.immunities.map((immunity, index) => (
              <span
                key={index}
                className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded"
              >
                {immunity}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};